import React, { useState } from "react";
import { Plus, Trash2, Crown, User } from "lucide-react";
import clsx from "clsx";
import { assignmentsApi } from "@/lib/api";

/**
 * Crew on a specific schedule block. Lead/role per-assignment. Supports add/remove
 * and toggle-lead. Mutation goes through assignmentsApi (per-block, not per-task).
 */
export default function CrewAssignmentsSection({ blockId, allCrew, assignments, ownerCrewId, onMutated }) {
  const [busy, setBusy] = useState(false);
  const [adderOpen, setAdderOpen] = useState(false);

  if (!blockId) {
    return (
      <div className="mono text-2xs text-ink-mute uppercase tracking-widest py-1">
        — task not scheduled —
      </div>
    );
  }

  const assignedIds = new Set(assignments.map((a) => a.crew_member_id));
  const available = allCrew.filter((c) => !assignedIds.has(c.id));

  const addCrew = async (crewId, isLead) => {
    setBusy(true);
    try {
      // role_on_block is null for leads (LEAD text comes from is_lead);
      // for non-leads we use the default "crew" role. Custom roles are
      // assigned via the grid picker which knows the project's role list.
      await assignmentsApi.create(blockId, {
        crew_member_id: crewId,
        role_on_block: isLead ? null : "crew",
        is_lead: !!isLead,
      });
      setAdderOpen(false);
      onMutated && onMutated();
    } finally {
      setBusy(false);
    }
  };

  const removeAssignment = async (a) => {
    setBusy(true);
    try {
      await assignmentsApi.remove(a.id);
      onMutated && onMutated();
    } finally {
      setBusy(false);
    }
  };

  const toggleLead = async (a) => {
    setBusy(true);
    try {
      // No PATCH endpoint for assignments — remove + re-create with flipped lead.
      await assignmentsApi.remove(a.id);
      await assignmentsApi.create(blockId, {
        crew_member_id: a.crew_member_id,
        role_on_block: a.is_lead ? "crew" : null,
        is_lead: !a.is_lead,
      });
      onMutated && onMutated();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="crew-assignments-section">
      {assignments.length === 0 && (
        <div className="mono text-2xs text-ink-mute uppercase tracking-widest py-1">
          — no crew assigned —
        </div>
      )}

      {assignments.map((a) => {
        const c = allCrew.find((x) => x.id === a.crew_member_id);
        const isOwner = a.crew_member_id === ownerCrewId;
        return (
          <div
            key={a.id}
            className="flex items-center gap-2 p-2 border border-line bg-bg-elevated"
            data-testid={`assignment-row-${a.id}`}
          >
            <button
              onClick={() => toggleLead(a)}
              disabled={busy}
              className={clsx(
                "shrink-0 w-5 h-5 flex items-center justify-center border transition-colors",
                a.is_lead
                  ? "border-amber bg-amber-glow text-amber"
                  : "border-line text-ink-mute hover:border-amber hover:text-amber"
              )}
              title={a.is_lead ? "Click to unmark as lead" : "Click to mark as lead"}
              data-testid={`assignment-lead-${a.id}`}
            >
              <Crown className="w-2.5 h-2.5" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-ink truncate">
                {c?.name || a.crew_member_id}
                {isOwner && <span className="ml-1 mono text-2xs text-amber">OWNER</span>}
                {a.is_lead && <span className="ml-1 mono text-2xs text-amber">LEAD</span>}
              </div>
              {c?.role && (
                <div className="mono text-2xs text-ink-mute uppercase tracking-wider mt-0.5">
                  {c.role}
                </div>
              )}
            </div>
            <button
              onClick={() => removeAssignment(a)}
              disabled={busy}
              className="btn-ghost text-ink-mute hover:text-red"
              title="Remove from this block"
              data-testid={`assignment-remove-${a.id}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        );
      })}

      {adderOpen ? (
        <div className="border border-amber p-2 bg-bg-elevated">
          <div className="mono text-2xs uppercase tracking-widest text-amber mb-2">PICK CREW</div>
          <div className="max-h-48 overflow-auto space-y-1">
            {available.length === 0 && (
              <div className="mono text-2xs text-ink-mute uppercase tracking-widest py-2">
                — all crew already assigned —
              </div>
            )}
            {available.map((c) => (
              <button
                key={c.id}
                onClick={() => addCrew(c.id, false)}
                disabled={busy}
                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-bg-highlight border border-transparent hover:border-amber text-left transition-colors"
                data-testid={`assignment-add-${c.id}`}
              >
                <User className="w-3 h-3 text-ink-mute" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-ink truncate">{c.name}</div>
                  <div className="mono text-2xs text-ink-mute uppercase tracking-wider">{c.role || "crew"}</div>
                </div>
              </button>
            ))}
          </div>
          <button onClick={() => setAdderOpen(false)} className="btn-ghost w-full justify-center mt-2 text-2xs">
            CANCEL
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdderOpen(true)}
          disabled={busy || available.length === 0}
          className="btn-ghost w-full justify-center"
          data-testid="assignment-add-btn"
        >
          <Plus className="w-3 h-3" /> ADD CREW
        </button>
      )}
    </div>
  );
}
